import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Checkbox,
  Typography,
  Tooltip,
} from '@mui/material';
import { SearchIcon } from 'components/SVG';
import { useSparkSearchContext } from 'hooks/common/useSparkSearchContextV2';
import { usePopover } from 'hooks/common';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useGetCategories } from 'hooks/Categories';
import { SparkButton, SparkEllipsisTypography } from 'sharedComponents/UI';
import type { GridProps } from '@mui/material';

type SearchDropdownWrapperProps = {
  children: React.ReactNode;
  selected: string | string[];
  title: string;
  height?: string;
  sx?: any;
};

export const SearchDropdownWrapper = ({
  children,
  selected,
  title,
  height = '2rem',
  sx = {},
}: SearchDropdownWrapperProps) => {
  const { open, openPopover, anchorEl, closePopover } = usePopover();
  const tooltip = React.useMemo(() => {
    if (Array.isArray(selected)) {
      return selected.join(', ');
    }
    return selected;
  }, [selected]);

  const handleMenuClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!Array.isArray(selected)) {
      closePopover();
    }
    const target = e.target as HTMLElement;

    if (target.id === 'category-spark-search-filter-button') {
      closePopover();
    }
  };

  const displayValue = React.useMemo(() => {
    if (!selected) {
      return title;
    }

    if (!Array.isArray(selected)) {
      return selected;
    }
    if (!selected.length) {
      return title;
    }
    if (selected.length === 1) {
      return selected;
    }
    return `${selected.length} Selected`;
  }, [title, selected]);

  const isFilterApplied = React.useMemo(() => {
    if (selected === 'Any') {
      return false;
    }

    return !!selected.length;
  }, [selected]);

  return (
    <Grid item xs={3} sx={{ cursor: 'pointer', ...sx }}>
      <Tooltip title={tooltip}>
        <Box
          bgcolor='white'
          height={height}
          onClick={openPopover}
          padding={1}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          borderRadius='10px'
          border={(theme) => `1px solid ${theme.palette.borderColor}`}
          sx={{
            '&:hover': {
              borderColor: (theme) => theme.palette.primary.main,
            },
          }}
        >
          <Box width='100%' padding={1}>
            {!isFilterApplied && (
              <Typography variant='body2' fontFamily='Mont-semibold'>
                {title}
              </Typography>
            )}
            {isFilterApplied && (
              <Box>
                {title && (
                  <Typography
                    variant='caption'
                    sx={{ color: (theme) => theme.palette.labelColor }}
                  >
                    {title}
                  </Typography>
                )}
                <SparkEllipsisTypography
                  variant='body2'
                  sx={{
                    WebkitLineClamp: 1,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    wordBreak: 'break-word',
                    fontFamily: 'Mont-semibold',
                  }}
                >
                  {displayValue}
                </SparkEllipsisTypography>
              </Box>
            )}
          </Box>
          <Box
            sx={{ cursor: 'pointer' }}
            display='flex'
            justifyContent='center'
            alignItems='center'
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        </Box>
      </Tooltip>
      <Menu
        open={open}
        onClose={closePopover}
        anchorEl={anchorEl}
        onClick={handleMenuClick}
        sx={{ maxHeight: '30rem', '& ul': { paddingBottom: 0 } }}
      >
        {children}
      </Menu>
    </Grid>
  );
};

type DropdownOption = {
  label: string;
  value: string;
};

type SparkMainAppSearchFiltersProps = {
  placeholder?: string;
  onChange?: (query: string) => void;
  dropdownOptionsFilter?: DropdownOption[];
  shouldRenderRating?: boolean;
  shouldRenderPrice?: boolean;
  shouldHideAllFilters?: boolean;
  containerStyles?: GridProps['sx'];
  textFieldContainerStyles?: GridProps['sx'];
  customEndAdornment?: React.ReactNode;
};

const sparkTypeDropdownOptions = [
  { label: 'All Sparks', value: 'allSparks' },
  { label: 'Workspace Sparks', value: 'workspaceSparks' },
  { label: 'My Sparks', value: 'mySparks' },
  { label: 'Favourite Sparks', value: 'favouriteSparks' },
];

function SparkMainAppSearchFilters({
  placeholder = '',
  onChange,
  dropdownOptionsFilter = sparkTypeDropdownOptions,
  shouldRenderRating = true,
  shouldRenderPrice = true,
  shouldHideAllFilters = false,
  containerStyles = {},
  textFieldContainerStyles = {},
  customEndAdornment,
}: SparkMainAppSearchFiltersProps) {
  const [localQuery, setLocalQuery] = React.useState('');
  const { dropdownOptions } = useGetCategories();

  const {
    handleCategoryChange,
    handleQueryChange,
    handleItemTypeChange,
    handleRattingChange,
    handlePriceChange,
    filter,
  } = useSparkSearchContext();

  // Sync localQuery with filter.search
  React.useEffect(() => {
    if (filter.search !== localQuery) {
      setLocalQuery(filter.search || '');
    }
  }, [filter.search]);

  const [locCategories, setLocCategories] = React.useState<string[]>(
    filter.category || []
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const query = e.target.value;

    setLocalQuery(query);

    handleQueryChange(query);
    onChange && onChange(query);
  };

  const handleCloseClick = () => {
    setLocalQuery('');
    handleQueryChange('');
  };

  const selectedCategoryLabels = filter.category
    ?.map((filterCategory) => {
      return dropdownOptions.find((option) => option.label === filterCategory)
        ?.label;
    })
    .filter((item): item is string => !!item);

  const rattingOptions = [
    { label: 'Any', value: 'any' },
    { label: '1+', value: 1 },
    { label: '2+', value: 2 },
    { label: '3+', value: 3 },
    { label: '4+', value: 4 },
    { label: '5', value: 5 },
  ];
  const priceOptions = [
    { label: 'Any', value: 'any' },
    { label: 'Free', value: 'free' },
    { label: '$5', value: 5 },
    { label: '$10', value: 20 },
    { label: '$25', value: 35 },
    { label: '$50', value: 50 },
  ];

  const handleLocCategoryUpdate = (categoryID: string) => {
    setLocCategories((prev) => {
      const exists = prev.includes(categoryID);
      if (exists) {
        return prev.filter((category) => category !== categoryID);
      } else {
        return [...prev, categoryID];
      }
    });
  };

  return (
    <Grid
      container
      item
      display='flex'
      alignItems='center'
      gap={2}
      marginTop={2}
      md={12}
      sx={containerStyles}
    >
      <Grid
        item
        container
        md={12}
        marginTop={2}
        flexWrap='nowrap'
        sx={textFieldContainerStyles}
      >
        <TextField
          placeholder={placeholder || 'Search...'}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #EEEEEE',
            borderRadius: '12px',
          }}
          autoComplete='off'
          fullWidth={true}
          onChange={handleChange}
          value={localQuery}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment:
              customEndAdornment ||
              (!!localQuery.length && (
                <IconButton onClick={handleCloseClick}>
                  <CloseIcon />
                </IconButton>
              )),
          }}
        />
      </Grid>
      {!shouldHideAllFilters && (
        <Grid
          item
          container
          xs={12}
          gap={2}
          flexWrap='nowrap'
          justifyContent={`${!shouldRenderPrice && !shouldRenderRating ? 'flex-start' : 'space-between'}`}
        >
          <SearchDropdownWrapper
            selected={
              dropdownOptionsFilter?.find(
                (item) => item.value === filter.selectedItemType
              )?.label || ''
            }
            title={
              filter.selectedItemType === dropdownOptionsFilter[0].value
                ? ''
                : dropdownOptionsFilter[0].label
            }
          >
            {dropdownOptionsFilter?.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleItemTypeChange(option.value as string)}
              >
                <Typography>{option.label}</Typography>
              </MenuItem>
            ))}
          </SearchDropdownWrapper>
          <SearchDropdownWrapper
            selected={selectedCategoryLabels || []}
            title='Category'
          >
            {dropdownOptions.map((category) => (
              <MenuItem
                key={category.value}
                onClick={() =>
                  handleLocCategoryUpdate(category.value as string)
                }
              >
                <Checkbox
                  checked={locCategories.includes(category.value as string)}
                  size='small'
                />
                <Typography variant='subtitle1' marginTop={0.5}>
                  {category.label}
                </Typography>
              </MenuItem>
            ))}
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                right: 0,
                left: 0,
                padding: 2,
                paddingTop: 0,
                bgcolor: 'white',
              }}
            >
              <SparkButton
                fullWidth={true}
                variant='contained'
                onClick={() => handleCategoryChange(locCategories)}
                sx={{ padding: 2 }}
                id='category-spark-search-filter-button'
              >
                Done
              </SparkButton>
            </Box>
          </SearchDropdownWrapper>
          {shouldRenderRating && (
            <SearchDropdownWrapper
              selected={
                rattingOptions.find((item) => item.value === filter.minRatting)
                  ?.label || ''
              }
              title='Rating'
            >
              {rattingOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => handleRattingChange(option.value as string)}
                >
                  <Typography>{option.label}</Typography>
                </MenuItem>
              ))}
            </SearchDropdownWrapper>
          )}
          {shouldRenderPrice && (
            <SearchDropdownWrapper
              selected={
                priceOptions.find((item) => item.value === filter.maxPrice)
                  ?.label || ''
              }
              title='Max Price'
            >
              {priceOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => handlePriceChange(option.value as string)}
                >
                  <Typography>{option.label}</Typography>
                </MenuItem>
              ))}
            </SearchDropdownWrapper>
          )}
        </Grid>
      )}
    </Grid>
  );
}

export default SparkMainAppSearchFilters;
